import { TableViewState, CURRENT_TABLE_VIEW_STATE_VERSION } from './types';
import { devWarn } from './env';

type Upgrader = (old: Record<string, unknown>) => Record<string, unknown>;

/**
 * Version upgrade steps keyed by the version they upgrade *from* (vN → vN+1).
 * Empty until a v2 shape exists; adding a step here lets readers transparently
 * upgrade rows written by an older client. See `./table-view-state-versioning.md`.
 */
const UPGRADERS: Record<number, Upgrader> = {};

/**
 * Normalize any stored value to the latest {@link TableViewState}, running the
 * upgrader chain as needed. Returns `null` when the value is absent or
 * unrecognizable — the caller then falls back to declared column-meta defaults.
 * Unknown fields on a valid state are preserved.
 */
export function upgradeTableViewState(raw: unknown): TableViewState | null {
  if (raw == null) return null;
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    devWarn('Ignoring malformed TableViewState (expected an object).');
    return null;
  }

  let current = raw as Record<string, unknown>;
  const version = typeof current.v === 'number' ? current.v : NaN;

  if (version > CURRENT_TABLE_VIEW_STATE_VERSION) {
    devWarn(`TableViewState v${version} is newer than supported v${CURRENT_TABLE_VIEW_STATE_VERSION}; using defaults.`);
    return null;
  }

  // Bounded by construction (at most CURRENT − version steps): an upgrader that
  // fails to advance `v` is caught by the final validation instead of looping.
  for (let from = version; from < CURRENT_TABLE_VIEW_STATE_VERSION; from += 1) {
    const step = UPGRADERS[from];
    if (!step) break;
    current = step(current);
  }

  if (current.v !== CURRENT_TABLE_VIEW_STATE_VERSION || !Array.isArray(current.visible)) {
    devWarn('Ignoring unrecognizable TableViewState (unknown version or missing visible[]).');
    return null;
  }

  return current as unknown as TableViewState;
}
